/**
 * Impulse Response — light-themed Canvas2D paint layer for mobile's Graphs
 * tab. See `../impulseResponse.ts` for the FFT-based reconstruction from the
 * measured complex frequency response (band-limited 20-20 kHz), and
 * `web/src/components/room/charts/impulseResponseDraw.ts` for the dark-dock
 * equivalent this mirrors the shape of (different ink, same geometry call).
 *
 * Linear time (ms) x-axis, NOT log-frequency — this is the one place in
 * `draw/` that uses `drawLinearAxisGrid` instead of `drawLogFreqAxisGrid`.
 */
import { computeImpulseResponseGeometry, type ImpulseResponseOptions } from '../impulseResponse';
import type { ChartPad } from '../layout';
import { setupCanvas, drawEmptyMessage, drawLinearAxisGrid, drawSeriesLine, INK } from './_shared';

export const IMPULSE_RESPONSE_PAD: ChartPad = { t: 10, r: 14, b: 20, l: 34 };

export interface DrawImpulseResponseOpts {
  freqs: number[];
  mag: number[];
  phase: number[];
  width: number;
  height: number;
  options?: ImpulseResponseOptions;
}

export function drawImpulseResponseCanvas(canvas: HTMLCanvasElement, opts: DrawImpulseResponseOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeImpulseResponseGeometry({
    freqs: opts.freqs,
    mag: opts.mag,
    phase: opts.phase,
    width: opts.width,
    height: opts.height,
    pad: IMPULSE_RESPONSE_PAD,
    mode: 'impulse',
    options: opts.options,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No phase data yet');
    return;
  }

  drawLinearAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks);
  drawSeriesLine(ctx, geo.frame, geo.points, INK.primary, 1.6);
}
